// Badge definitions and gamification constants

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string; // emoji
  requirement: {
    type: 'items_saved' | 'zero_waste_streak' | 'scans' | 'money_saved' | 'co2_saved' | 'challenge_complete';
    threshold: number;
  };
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

export interface Challenge {
  id: string;
  name: string;
  description: string;
  icon: string;
  duration: number; // days
  goal: {
    type: 'zero_waste' | 'save_items' | 'scan_items';
    target: number;
  };
  reward: {
    badgeId?: string;
    message: string;
  };
}

export const BADGES: Badge[] = [
  // Common badges
  {
    id: 'first_save',
    name: 'First Save',
    description: 'Marked your first item as used',
    icon: '🌱',
    requirement: { type: 'items_saved', threshold: 1 },
    rarity: 'common',
  },
  {
    id: 'scanner',
    name: 'Scanner',
    description: 'Scanned 5 barcodes',
    icon: '📱',
    requirement: { type: 'scans', threshold: 5 },
    rarity: 'common',
  },
  {
    id: 'waste_warrior',
    name: 'Waste Warrior',
    description: 'Saved 10 items from waste',
    icon: '⚔️',
    requirement: { type: 'items_saved', threshold: 10 },
    rarity: 'common',
  },

  // Rare badges
  {
    id: 'zero_waste_week',
    name: 'Zero Waste Week',
    description: 'No wasted items for 7 consecutive days',
    icon: '🏆',
    requirement: { type: 'zero_waste_streak', threshold: 7 },
    rarity: 'rare',
  },
  {
    id: 'scan_master',
    name: 'Scan Master',
    description: 'Scanned 50 barcodes',
    icon: '🔍',
    requirement: { type: 'scans', threshold: 50 },
    rarity: 'rare',
  },
  {
    id: 'food_saver',
    name: 'Food Saver',
    description: 'Saved 50 items from waste',
    icon: '💚',
    requirement: { type: 'items_saved', threshold: 50 },
    rarity: 'rare',
  },

  // Epic badges
  {
    id: 'money_saver',
    name: 'Money Saver',
    description: 'Saved $100 worth of food',
    icon: '💰',
    requirement: { type: 'money_saved', threshold: 100 },
    rarity: 'epic',
  },
  {
    id: 'eco_warrior',
    name: 'Eco Warrior',
    description: 'Saved 100 items from waste',
    icon: '🌍',
    requirement: { type: 'items_saved', threshold: 100 },
    rarity: 'epic',
  },
  {
    id: 'scan_legend',
    name: 'Scan Legend',
    description: 'Scanned 200 barcodes',
    icon: '⭐',
    requirement: { type: 'scans', threshold: 200 },
    rarity: 'epic',
  },

  // Legendary badges
  {
    id: 'zero_waste_champion',
    name: 'Zero Waste Champion',
    description: 'No wasted items for 30 consecutive days',
    icon: '👑',
    requirement: { type: 'zero_waste_streak', threshold: 30 },
    rarity: 'legendary',
  },
  {
    id: 'planet_protector',
    name: 'Planet Protector',
    description: 'Saved 500 items from waste',
    icon: '🌟',
    requirement: { type: 'items_saved', threshold: 500 },
    rarity: 'legendary',
  },
];

export const CHALLENGES: Challenge[] = [
  {
    id: 'zero_waste_week_challenge',
    name: 'Zero Waste Week',
    description: 'Don\'t waste any food for 7 days',
    icon: '🏆',
    duration: 7,
    goal: { type: 'zero_waste', target: 0 },
    reward: {
      badgeId: 'zero_waste_week',
      message: 'Congratulations! You\'ve earned the Zero Waste Week badge!',
    },
  },
  {
    id: 'save_20_challenge',
    name: 'Save 20 Items',
    description: 'Mark 20 items as used this month',
    icon: '🎯',
    duration: 30,
    goal: { type: 'save_items', target: 20 },
    reward: {
      message: 'Amazing! You saved 20 items from waste!',
    },
  },
  {
    id: 'scan_master_challenge',
    name: 'Scan Master',
    description: 'Scan 10 items this week',
    icon: '📱',
    duration: 7,
    goal: { type: 'scan_items', target: 10 },
    reward: {
      message: 'You\'re a scanning pro!',
    },
  },
];

// Calculate CO2 savings (rough estimate: 1 item = 2.5 kg CO2)
export function calculateCO2Savings(itemCount: number): number {
  return Math.round(itemCount * 2.5 * 10) / 10; // Round to 1 decimal
}

// Get badge rarity color
export function getBadgeRarityColor(rarity: Badge['rarity']): string {
  switch (rarity) {
    case 'common':
      return '#9CA3AF'; // gray
    case 'rare':
      return '#3B82F6'; // blue
    case 'epic':
      return '#A855F7'; // purple
    case 'legendary':
      return '#F59E0B'; // gold
    default:
      return '#9CA3AF';
  }
}

// Check if badge should be awarded
export function shouldAwardBadge(badge: Badge, stats: any): boolean {
  switch (badge.requirement.type) {
    case 'items_saved':
      return stats.totalItemsSaved >= badge.requirement.threshold;
    case 'zero_waste_streak':
      return stats.currentZeroWasteStreak >= badge.requirement.threshold;
    case 'scans':
      return stats.totalScans >= badge.requirement.threshold;
    case 'money_saved':
      return stats.totalMoneySaved >= badge.requirement.threshold;
    case 'co2_saved':
      return stats.totalCO2Saved >= badge.requirement.threshold;
    default:
      return false;
  }
}
